import { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Deployment tier (build-time). Mirrors the API's `surface` split so the public
// site and the internal tools ship as separate, independently-gated artifacts:
//   public — marketing + FindCare only, served from int.starthealth.com and
//            findcare.starthealth.com (both point at the public-web/public-api
//            services -- two hostnames, one deployment, no branching needed
//            since this tier never varies its routes by host)
//   portal — Project Mgmt + Broker Discovery only, served from
//            bolt.starthealth.com alone (internal, network-gated)
//   all    — both (local dev default)
// Every tier is a LAZY module whose dynamic import() is gated on a build-time
// constant, so a build only ever references — and Vite only ever emits — the
// code for the tiers it includes. True isolation in both directions.
//
// Each tier maps to exactly one hostname-to-service pairing (see the
// companion k8s-infra HTTPRoutes), so — unlike an earlier version of this
// file — there's no runtime hostname check here: which routes exist is
// decided entirely by which tier was built, not by what host answered the
// request.
// ---------------------------------------------------------------------------
const TIER = (import.meta.env.VITE_APP_TIER as string) || 'all';
const includePublic = TIER === 'public' || TIER === 'all';
const includePortal = TIER === 'portal' || TIER === 'all';

const PublicApp = includePublic ? lazy(() => import('./PublicApp')) : null;
const ProjectsApp = includePortal ? lazy(() => import('./internal/ProjectsApp')) : null;
const MarketingApp = includePortal ? lazy(() => import('./internal/MarketingApp')) : null;
// No-login ticket intake, deployed alongside the portal tier (where the
// staff who'd use it actually are) but deliberately NOT nested under
// ProjectsApp/MarketingApp — both require a Start-auth session.
const ReportIssuePage = includePortal ? lazy(() => import('./pages/ReportIssuePage')) : null;

function Fallback() {
  return (
    <div className="flex min-h-screen items-center justify-center font-body text-body-md text-on-surface-variant">
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      {includePortal && ProjectsApp && MarketingApp && ReportIssuePage && (
        <>
          {/* Root, not /projects -- bolt.starthealth.com is this app's only
              hostname, so there's no /marketing sibling to disambiguate from
              at the top level the way there was when int.starthealth.com
              also served this tier. */}
          <Route
            path="/*"
            element={<Suspense fallback={<Fallback />}><ProjectsApp /></Suspense>}
          />
          <Route
            path="/marketing/*"
            element={<Suspense fallback={<Fallback />}><MarketingApp /></Suspense>}
          />
          <Route
            path="/report-issue"
            element={<Suspense fallback={<Fallback />}><ReportIssuePage /></Suspense>}
          />
        </>
      )}

      {/* Only reachable in the local/dev VITE_APP_TIER=all build together with
          the portal routes above -- ProjectsApp's "/*" wins there (declared
          first), so PublicApp is unreachable under tier=all. Never an issue in
          production: each deployed image is single-tier. */}
      {includePublic && PublicApp && (
        <Route path="/*" element={<Suspense fallback={<Fallback />}><PublicApp /></Suspense>} />
      )}
    </Routes>
  );
}
